import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useMenuPermission } from '@src/recoil/permission';
import { useRecoilState } from 'recoil';
import { categoryId, menuScroll } from '@src/recoil/menu';
import { CallGetCategories, CallDeleteCategory, CallDeleteMenu } from '@library/request/callProduct';
import { MenuEmptyBox, BottomPopup, NoAuthBox } from '@src/pages/product/componnents/common';
import { BottomPopupType, Categories, MenuManageTabProps, Product, PRODUCT_KEYS } from '@type/productType';

// 메뉴관리
const MenuManagement = (props: MenuManageTabProps) => {
  const { storeId, setLoading } = props;
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { menuPermission, isNoAuth1, isNoAuthMenu } = useMenuPermission(storeId);

  const [isBottomPopup, setIsBottomPopup] = useState<boolean>(false); // 하단 팝업 노출 유무
  const [type, setType] = useState<BottomPopupType>('category'); // 카테고리 or 메뉴
  const [selectedCategory, setSelectedCategory] = useState<Categories>(null); // 선택한 카테고리

  const clickedListRef = useRef<{ id: string; title: string }>(null); // 클릭한 카테고리 or 메뉴
  const categoryIdRef = useRef<string>(''); // 클릭한 카테고리 ID

  const [currentCategoryId, setCategoryId] = useRecoilState(categoryId);
  const [menuScrollValue, setMenuScrollValue] = useRecoilState(menuScroll);

  // 카테고리 리스트 조회 API
  const { data: categoryList } = useQuery([PRODUCT_KEYS.CATEGORY, storeId], {
    enabled: !!storeId && menuPermission.isFetch && (!menuPermission.data.length || !isNoAuthMenu),
    queryFn: async () => {
      try {
        setLoading(true);
        const response = await CallGetCategories(storeId);
        if (response?.result.code !== 1) return;
        const { data } = response;
        return data;
      } catch (error) {
        console.log('error', error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
  });

  // 카테고리 삭제 API
  const { mutate: categoryMutate } = useMutation((categoryID: string) => CallDeleteCategory(storeId, categoryID), {
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: (response) => {
      const { result } = response;
      if (result.code !== 1) {
        alert(result.message);
      } else {
        // 카테고리 재조회
        queryClient.invalidateQueries([PRODUCT_KEYS.CATEGORY, storeId]);
      }
    },
    onError: (error) => {
      console.log('error:', error);
    },
    onSettled: () => {
      setLoading(false);
      setIsBottomPopup(false);
    },
  });

  // 메뉴 삭제 API
  const { mutate: menuMutate } = useMutation((productsID: string) => CallDeleteMenu(storeId, productsID), {
    onMutate: () => {
      setLoading(true);
    },
    onSuccess: (response) => {
      const { result } = response;
      if (result.code !== 1) {
        alert(result.message);
      } else {
        // 카테고리 재조회
        queryClient.invalidateQueries([PRODUCT_KEYS.CATEGORY, storeId]);
      }
    },
    onError: (error) => {
      console.log('error:', error);
    },
    onSettled: () => {
      setLoading(false);
      setIsBottomPopup(false);
    },
  });

  // 카테고리/메뉴 삭제
  const onClickDelete = () => {
    if (!clickedListRef.current.id) return;

    const isCategory = type === 'category';
    const contents = isCategory
      ? '해당 카테고리를 삭제하시겠습니까?\n삭제 후 다시 취소하실 수 없습니다.'
      : '해당 메뉴를 삭제하시겠습니까?\n삭제 후 다시 취소하실 수 없습니다.';

    if (window.confirm(contents)) {
      isCategory ? categoryMutate(clickedListRef.current.id) : menuMutate(clickedListRef.current.id);
    }
  };

  useEffect(() => setType('category'), [storeId]);

  useEffect(() => {
    if (!categoryList || !categoryIdRef.current) return;

    const newCategory = categoryList.find((category) => category.categoryID === categoryIdRef.current);
    setSelectedCategory(newCategory ?? null);
  }, [categoryIdRef.current, categoryList]);

  useEffect(() => {
    if (currentCategoryId) {
      categoryIdRef.current = currentCategoryId;
      setType('menu');
    }
  }, []);

  useEffect(() => {
    if (type === 'category' || menuScrollValue.depth1 > 0) {
      window.scrollTo(0, menuScrollValue.depth1);
    }
    if (type === 'menu' || menuScrollValue.depth2 > 0) {
      window.scrollTo(0, menuScrollValue.depth2);
    }
  }, [type]);

  const ListElement = ({ list }: { list: (Categories | Product)[] }) => {
    const isCategory = (item: Categories | Product): item is Categories => {
      return (item as Categories).categoryID !== undefined;
    };

    return (
      <ul>
        {list.map((item) => {
          const id = isCategory(item) ? item.categoryID : item.productID;
          const title = isCategory(item) ? item.categoryName : item.name;

          return (
            <li
              key={id}
              className={`${!isCategory(item) && item.isDisplay === '0' ? 'off' : ''} ${
                !isCategory(item) && Number(item.status) === 4 ? 'soldout' : ''
              }`}
            >
              <div className="item-product" data-product-id={id}>
                <button
                  type="button"
                  className="txt-product"
                  onClick={() => {
                    if (isCategory(item)) {
                      setMenuScrollValue({
                        depth1: window.scrollY,
                        depth2: 0,
                      });
                      setType('menu');
                      clickedListRef.current = { id, title };
                      categoryIdRef.current = id;
                      setCategoryId(id);
                    } else {
                      setMenuScrollValue({
                        depth1: menuScrollValue.depth1,
                        depth2: window.scrollY,
                      });
                      navigate(`/product/info?storeID=${storeId}&productID=${id}`);
                    }
                  }}
                >
                  {title}
                  <span className="txt-state" />
                </button>
                <button
                  type="button"
                  className="li-btn-more"
                  onClick={() => {
                    clickedListRef.current = { id, title };
                    setIsBottomPopup(true);
                  }}
                >
                  <span className="ico-more" />
                  더보기
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div>
      {isNoAuthMenu ? (
        <NoAuthBox type="menu" />
      ) : (
        <>
          {type === 'category' ? (
            // 카테고리
            <div className="box-content">
              <h4 className="tit-product">카테고리</h4>
              <button
                type="button"
                className="btn-floating"
                disabled={isNoAuth1}
                onClick={() => {
                  setMenuScrollValue({
                    depth1: window.scrollY,
                    depth2: 0,
                  });
                  navigate(`/product/category?storeID=${storeId}`);
                }}
              >
                <span className="ico-plus" />
                카테고리 추가
              </button>
              {categoryList ? <ListElement list={categoryList} /> : <MenuEmptyBox type="category" />}
            </div>
          ) : (
            // 메뉴
            <div className="box-content">
              <h4 className="tit-product">
                <button
                  type="button"
                  className="link-back"
                  onClick={() => {
                    setType('category');
                    setCategoryId('');
                  }}
                >
                  <span className="ico-arr" />
                </button>
                <span className="category-name">{selectedCategory?.categoryName}</span>
              </h4>
              <button
                type="button"
                className="btn-floating"
                disabled={isNoAuth1}
                onClick={() => {
                  setMenuScrollValue({
                    depth1: menuScrollValue.depth1,
                    depth2: window.scrollY,
                  });
                  navigate(`/product/form?storeID=${storeId}&categoryID=${selectedCategory?.categoryID}`);
                }}
              >
                <span className="ico-plus" />
                메뉴 추가
              </button>
              {selectedCategory?.products?.length ? (
                <ListElement list={selectedCategory.products} />
              ) : (
                <MenuEmptyBox type="menu" />
              )}
            </div>
          )}
          {/* 더보기 팝업 */}
          {clickedListRef.current && (
            <BottomPopup
              isBottomPopup={isBottomPopup}
              onClickClose={() => setIsBottomPopup(false)}
              type={type}
              clickedList={clickedListRef.current}
              onClickDelete={onClickDelete}
            />
          )}
        </>
      )}
    </div>
  );
};

export default MenuManagement;
